/**
 * tracking_utils.js
 *
 * A component that contains all the common utility functions for tracking.
 */
import liTrackClient from '../linkedin/li-track-client.min';

  // Dependencies.
  var win = window;
  var ss = win.slideshare_object,
    utils = ss.utils,
    urnMap;

  urnMap = {
      'Clip': 'urn:li:slideShareClip:',
      'Clipboard': 'urn:li:slideShareClipboard:',
      'Guest': 'urn:li:slideShareGuest:',
      'LyndaCategory' : 'urn:li:lyndaCategory:' ,
      'LyndaCourse': 'urn:li:lyndaCourse:',
      'LyndaVideo': 'urn:li:lyndaVideo:',
      'LeadCampaign':'urn:li:slideShareLeadCampaign:',
      'Slideshow': 'urn:li:slideShareSlideshow:',
      'User': 'urn:li:slideShareUser:'
    };

  /**
   * Creates a new TrackingUtils instance.
   * @constructor
   */
  function TrackingUtils() {
    var header;
    this.APP_BASE = 'slideshare.js.tracking';

    this._bodyDefaults = {};
    if(ss.user && ss.user.id) {
      header = {
        header: {
          applicationViewerUrn: this.getUrn('User', ss.user.id)
        }
      };
      this._bodyDefaults = $.extend({}, this._bodyDefaults, header);
    }

    // Initialize Li-track-client
    if (liTrackClient !== 'undefined') {
      liTrackClient.setTrackingUrl();
      liTrackClient.setAppId(this.APP_BASE);
    }
  }

  TrackingUtils.prototype = /** @lends TrackingUtils.prototype */ {

    constructor: TrackingUtils,

    /**
     * Gets the appId for your app by prepending the APP_BASE
     *   getAppId() -> 'slideshare.js.tracking'
     *   getAppIg('clipping') -> 'slideshare.js.tracking.clipping'
     * @param {String} appName Name of your app, eg. clipping, leads
     * @returns {string}
     */
    getAppId: function(appName) {
      return appName ? this.APP_BASE + '.' + appName : this.APP_BASE;
    },

    /**
     * records a generic Li tracking event and adds the slideshare user id if
     * available to the applicationViewerURN field of the header (EventHeader)
     * @param  {String} appId Id of the app
     * @param  {String} eventName Name of the event
     * @param  {Object} eventBody Tracking metadata
     * @param  {Function} callback Optional callback. The signature
     *          expected is: callback(err, data). If err is null it
     *          means there were no errors.
     */
    recordTrackingEvent: function(appId, eventName, eventBody, callback) {
      var data;
      // Add body defaults to eventBody
      eventBody = $.extend({}, this._bodyDefaults, eventBody);

      if (window.liTrackClient) {
        data = {
          eventInfo: {
            eventName: eventName,
            appId: appId
          },
          eventBody: eventBody
        };
        // Use the line below for dev testing
        // console.log(JSON.stringify(data));
        // console.log(JSON.stringify(data, null, 2));
        if (callback) {
          liTrackClient.trackWithCallback(data, callback);
        } else {
          liTrackClient.track(data);
        }
      } else {
        throw new Error("liTrackClient is not initialized");
      }
    },

    /**
     * Intended replacement for SecureRandom.hex. Generates a random id of length
     * n (default 16) seeded with slideshow id and _uv_id cookie
     *
     * use for For high qps systems where a simple Math.random can cause a high
     * percentage of duplicates, example recommendations li tracking.
     * A simple Math.random should suffice for lower qps systems
     *
     * @param {Integer} n The length of the trackingId, default 16
     * @returns {String} TrackingId
     */
    generateTrackingID: function(n){
      var result = '', tmp_str, pad = '0000';
      n = n || 16;

      // Generate first 4 characters using the last 4 characters of slideshow_id
      if (typeof ss !== 'undefined'
        && typeof ss.slideshow !== 'undefined'
        && ss.slideshow.id !== null) {
        tmp_str = parseInt(ss.slideshow.id).toString(16).slice(-4);
        // We pad the result to have a more definitive schema
        result += (pad + tmp_str).slice(-pad.length);
      }

      // Generate next 4 characters of random string from using last 4 characters of _uv_id
      if (cookie('_uv_id') !== null) {
        // We pad the result to have a more definitive schema
        tmp_str = parseInt(cookie('_uv_id')).toString(16).slice(-4);
        result += (pad + tmp_str).slice(-pad.length);
      }
      n -= result.length;
      while(n--) {
        result += Math.floor(Math.random()*16).toString(16).toUpperCase();
      }
      return result;
    },

    /**
     * Generates a urn for an entity given the entity type and id. Returns null
     * if the entityType is not included in the internal map.
     * @param {String} entityType The type of entity as used in the urnMap.
     *                            If the entity type does not exist in the map,
     *                            urn:li:slideShare<entityType>:<entityId> will
     *                            be returned. (make sure the first word  of your
     *                            entityType is capitalized)
     * @param {String} entityId The id for this entity
     * @returns {String} Urn
     */
    getUrn: function(entityType, entityId) {
      if (!entityId) {
        return null;
      }
      if (urnMap[entityType]) {
        return urnMap[entityType] + entityId;
      }
      // Default behavior
      return 'urn:li:slideShare' + entityType + ':' + entityId;
    },

    /**
     * Extracts the page key from the ID of the body element.
     * @returns {String|undefined} The page key, if found, or undefined if none
     */
    getPageKey: function() {
      var match = /^pagekey-(.+)$/.exec(document.body.id);
      if (match) {
        return match[1];
      }
    },

    /**
     * Gets the _uv_id (unique visitor id) stored in the browser cookies.
     * @returns {String| undefined} The _uv_id cookie if found, or undefined if not
     */
    getSlideShareGuest: function() {
      return cookie('_uv_id');
    }

  };

export default TrackingUtils;
